import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

/**
 * @author rkassmullet
 */

import { ForwardFocusRef } from "abreact/incubator/ui/utils/FocusUtils";
import { NullUtils } from "abweb/utils/NullUtils";
import { ObjectUtils } from "abweb/utils/ObjectUtils";
import React, { useCallback, useContext, useEffect, useState } from "react";
export const AbGenericFocusManagerContext = /*#__PURE__*/React.createContext(undefined);
/**
 * useGenericFocusManager will register callbacks to imperatively manage the focus of a certain context.
 * This will allow any component within the context to be able to request/claim focus, or to set focus to
 * any other known targets.
 *
 * All registered focus targets are of Generic type TargetType.
 *
 * An example use for this is the case of a list of a rows, where each row can be deleted. If you delete a row,
 * you will need to set focus on a nearby object to maintain Keyboard Accessibility. Since you are deleting an object,
 * not adding a new item, the setting of autofocus on nearby items will not work to re-route Focus. So, you can
 * use this FocusManager to imperatively set focus  on the previous index, next next row, or perhaps the
 * 'add row' button.
 *
 **/
export function useAbGenericFocusManager() {
  const [currentFocusRequestTarget, setCurrentFocusRequestTarget] = useState(undefined);
  const isCurrentFocusRequest = useCallback(target => {
    return currentFocusRequestTarget !== undefined && ObjectUtils.deepCompare(currentFocusRequestTarget, target);
  }, [currentFocusRequestTarget]);
  const setFocusRequest = useCallback(target => {
    if (target && !isCurrentFocusRequest(target)) {
      setCurrentFocusRequestTarget(target);
    }
  }, [isCurrentFocusRequest]);
  return {
    currentFocusRequestTarget: currentFocusRequestTarget,
    isCurrentFocusRequest: isCurrentFocusRequest,
    setFocusRequest: setFocusRequest
  };
}
/**
 * useRegisterFocusTarget will register a ref as a potential target for assignment by the FocusManager
 *
 *@param targetRepresentation The TargetType keyed valued used to identify the focus Target, this should be unique
 **/
export function useRegisterFocusTarget(props) {
  const focusManager = NullUtils.assertDefined(useContext(AbGenericFocusManagerContext));
  const [forwardFocusRef] = useState(() => {
    return new ForwardFocusRef();
  });
  useEffect(() => {
    if (focusManager.isCurrentFocusRequest(props.targetRepresentation)) {
      forwardFocusRef.whenExistsTakeFocusOnce();
      focusManager.setFocusRequest(undefined);
    }
  }, [focusManager.currentFocusRequestTarget, focusManager.isCurrentFocusRequest]);
  return {
    managedFocusRef: forwardFocusRef
  };
}

/**
 * Class Hook to allow interoperability with Class components via prop injection.
 * Enforce that the class being wrapped implements the ManagedFocusModel
 **/
export function useComponentWithManagedFocus(Component, targetRepresentation) {
  const managedFocusModel = useRegisterFocusTarget({
    targetRepresentation
  });
  const renderComponentWithManagedFocus = useCallback(props => {
    return /*#__PURE__*/React.createElement(Component, _extends({}, props, managedFocusModel));
  }, [targetRepresentation]);
  return renderComponentWithManagedFocus;
}
//# sourceMappingURL=AbGenericFocusManager.js.map
